import React, {useCallback, useEffect, useMemo, useState} from 'react'
import axios from 'axios'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import toaster from '../../../Utils/toaster'
import {CommonLoading} from '../../../Utils/commonLoading'
import {KTSVG} from '../../../_metronic/helpers'
import {
  BatchDetail,
  BatchItem,
  ItemDraft,
  defaultDraftFromItem,
} from './bulkVosTypes'
import {
  formatBulkVosCallId,
  formatBulkVosInterpreterId,
  formatBulkVosRecordId,
  formatPeriodLabel,
  formatSubmitted,
  formatBulkVosGridTime,
  buildBulkVosPortalUrl,
} from './bulkVosClientPortalFormat'
import {BulkVosOsiEditLauncher} from './BulkVosOsiEditLauncher'
import {BulkVosVosFormViewIcon} from './BulkVosVosFormViewIcon'
import './bulkVosClientPortal.css'
import {BulkVosFinanceFilterDropdown} from './BulkVosFinanceFilterDropdown'
import {
  applyBulkVosFinanceItemFilters,
  BulkVosFinanceBatchOption,
  BulkVosFinanceFilterData,
} from './bulkVosFinanceFilter'
import {CommonPaginationModel} from '../../../Utils/commonPagination'

const API_URL = process.env.REACT_APP_API_URL
const defaultRowsPerPage =
  Number(process.env.REACT_APP_PAGINATION_ITEMS_PER_PAGE) || 10

type Props = {
  batch: BatchDetail | null
  detailLoading: boolean
  readOnly: boolean
  filterData: BulkVosFinanceFilterData
  availableBatches: BulkVosFinanceBatchOption[]
  onFilterApply: (next: BulkVosFinanceFilterData) => void
  onReload: () => Promise<void>
  onApproved?: () => void
}

function downloadBlob(blob: Blob, filename: string) {
  const url = window.URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  window.URL.revokeObjectURL(url)
}

function isRowDisputed(item: BatchItem, batchStatus?: number): boolean {
  if (batchStatus === 2) return false
  if (item.financeStatus === 3 || item.financeStatus === 4) return false
  return item.isDisputed || item.financeStatus === 2
}

function isOpenFinanceDispute(item: BatchItem, batchStatus?: number): boolean {
  if (batchStatus === 2) return false
  if (item.financeStatus === 3 || item.financeStatus === 4) return false
  if (item.financeStatus === 2) return true
  return Boolean(item.isDisputed)
}

type FinanceRowAction = 'dispute' | 'sent-pending' | 'accepted' | 'approved'

function resolveFinanceRemarks(item: BatchItem, draft: ItemDraft, disputed: boolean): string {
  const fromApi = item.remarks?.trim()
  if (fromApi) return fromApi
  if (disputed) return draft.remarks?.trim() ?? ''
  return ''
}

function FinanceRemarksCell({text, rowKey}: {text: string; rowKey: number}) {
  const trimmed = text.trim()
  if (!trimmed) {
    return <span className='text-gray-800 d-block fs-6'>—</span>
  }
  const long = trimmed.length > 72 || trimmed.includes('\n')
  const body = (
    <span className={`text-gray-800 d-block fs-6 bvp-finance-remarks${long ? ' bvp-finance-remarks--clamped' : ''}`}>
      {trimmed}
    </span>
  )
  if (!long) return body
  return (
    <OverlayTrigger
      placement='auto'
      overlay={
        <Tooltip id={`bvp-finance-remarks-${rowKey}`} className='bvp-finance-remarks-tooltip'>
          <span className='bvp-finance-remarks-tooltip-inner'>{trimmed}</span>
        </Tooltip>
      }
    >
      {body}
    </OverlayTrigger>
  )
}

function financeRowAction(
  item: BatchItem,
  batchStatus?: number,
  batchApproved?: boolean,
): FinanceRowAction {
  if (isRowDisputed(item, batchStatus)) return 'dispute'
  if (batchApproved || item.financeStatus === 4) return 'approved'
  if (batchStatus === 0 || item.financeStatus === 0) return 'sent-pending'
  return 'accepted'
}

export function BulkVosFinanceView({
  batch,
  detailLoading,
  readOnly,
  filterData,
  availableBatches,
  onFilterApply,
  onReload,
  onApproved,
}: Props) {
  const [query, setQuery] = useState('')
  const [osiEditItem, setOsiEditItem] = useState<BatchItem | null>(null)
  const [drafts, setDrafts] = useState<Record<number, ItemDraft>>({})
  const [items, setItems] = useState<BatchItem[]>([])
  const [approving, setApproving] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [rowsPerPage, setRowsPerPage] = useState(defaultRowsPerPage)

  useEffect(() => {
    setCurrentPage(1)
  }, [batch?.batchId, query, filterData])

  useEffect(() => {
    if (!batch) {
      setItems([])
      setDrafts({})
      return
    }
    setItems(batch.items)
    const next: Record<number, ItemDraft> = {}
    batch.items.forEach((i) => {
      next[i.id] = defaultDraftFromItem(i)
    })
    setDrafts(next)
  }, [batch])

  const getDraft = useCallback(
    (item: BatchItem) => drafts[item.id] ?? defaultDraftFromItem(item),
    [drafts],
  )

  const disputedCount = items.filter((i) => isRowDisputed(i, batch?.status)).length
  const openDisputes = items.filter((i) => isOpenFinanceDispute(i, batch?.status)).length

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    const withFilters = applyBulkVosFinanceItemFilters(items, filterData)
    if (!q) return withFilters
    const clientName = batch?.customerName ?? ''
    return withFilters.filter((i) =>
      [
        clientName,
        i.callId,
        formatBulkVosCallId(i.callId),
        i.requesterName,
        i.interpreterId,
        formatBulkVosInterpreterId(i.interpreterId),
        i.recordId,
        i.assignmentDate,
        i.communicationTypeLabel,
      ].some((v) => (v || '').toLowerCase().includes(q)),
    )
  }, [items, query, filterData, batch?.customerName])

  const totalItems = filtered.length
  const totalPages = Math.max(1, Math.ceil(totalItems / rowsPerPage) || 1)

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages)
    }
  }, [currentPage, totalPages])

  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * rowsPerPage
    return filtered.slice(start, start + rowsPerPage)
  }, [filtered, currentPage, rowsPerPage])

  const periodLabel = batch
    ? formatPeriodLabel(batch.servicePeriodStartUtc, batch.servicePeriodEndUtc)
    : ''
  const batchApproved = batch ? batch.status === 2 || readOnly : readOnly
  const partnerPortalUrl = useMemo(() => {
    const token = batch?.portalToken?.trim()
    if (!token) return null
    return buildBulkVosPortalUrl(token)
  }, [batch?.portalToken])

  const partnerPortalLinkLabel = useMemo(() => {
    if (!batch) return ''
    const parts: string[] = []
    if (batch.customerName?.trim()) parts.push(batch.customerName.trim())
    if (periodLabel) parts.push(`Service period: ${periodLabel}`)
    return parts.join(' · ')
  }, [batch, periodLabel])

  const downloadCsv = async () => {
    if (!batch) return
    try {
      const res = await axios.get(`${API_URL}/bulk-vos/batches/${batch.batchId}/export`, {
        responseType: 'blob',
      })
      downloadBlob(res.data, `bulk-vos-batch-${batch.batchId}.csv`)
    } catch {
      toaster('error', 'Download failed')
    }
  }

  const approveAll = async () => {
    if (!batch || readOnly || openDisputes > 0) return
    setApproving(true)
    try {
      const res = await axios.post(`${API_URL}/bulk-vos/approve-all/${batch.batchId}`)
      if (res.data?.status === 'S') {
        toaster('success', 'Batch approved')
        onApproved?.()
        await onReload()
      } else toaster('error', res.data?.text || 'Approve failed')
    } catch {
      toaster('error', 'Approve failed')
    } finally {
      setApproving(false)
    }
  }

  const canApproveAll = Boolean(batch) && !readOnly && batch!.status === 1 && openDisputes === 0
  let footerNote = 'Ready to approve batch'
  if (readOnly) footerNote = 'This batch is approved'
  else if (!batch) footerNote = 'No batch available to display'
  else if (openDisputes > 0)
    footerNote = `Resolve ${openDisputes} disputed record${openDisputes === 1 ? '' : 's'} before approving`
  else if (batch.status !== 1) footerNote = 'Waiting for client submission'

  const showStatusBar = Boolean(batch) && !readOnly && batch!.status === 1
  const canEditFinanceRows = Boolean(batch) && !readOnly && batch!.status === 1

  const closeOsiEdit = useCallback(() => setOsiEditItem(null), [])

  const openFinanceItemModal = (item: BatchItem) => {
    if (!canEditFinanceRows) return
    setOsiEditItem(item)
  }

  return (
    <>
      <div className='rounded'>
        <div className='mt-2 d-flex justify-content-between flex-wrap align-items-center gap-3'>
          <div>
            <h4 className='mb-1'>Finance Review</h4>
            <p className='text-muted fs-7 mb-0'>
              Audit financial adjustments, verify disputed agent penalties, and approve final payout
              calculations.
            </p>
          </div>
          <div className='d-flex flex-wrap align-items-center gap-2'>
            <div className='d-flex align-items-center position-relative flex-nowrap'>
              <input
                type='text'
                data-kt-user-table-filter='search'
                className='form-control form-control-white form-control-sm max-w-250px custom-search-radius'
                placeholder='Search'
                value={query}
                onChange={(e) => {
                  setQuery(e.target.value)
                  setCurrentPage(1)
                }}
              />
              <button
                type='button'
                className='btn btn-primary btn-icon btn-sm custom-search-btn-radius px-3'
                aria-label='Search'
                onClick={() => setCurrentPage(1)}
              >
                <KTSVG path='/media/icons/duotune/general/gen021.svg' className='' />
              </button>
            </div>
            <OverlayTrigger
              placement='top'
              overlay={<Tooltip id='bulk-vos-download'>Download CSV</Tooltip>}
            >
              <button
                type='button'
                className='btn btn-primary btn-sm btn-icon'
                disabled={!batch}
                onClick={() => void downloadCsv()}
              >
                <KTSVG path='/media/icons/duotune/files/fil017.svg' className='svg-icon-muted' />
              </button>
            </OverlayTrigger>
            <div className='d-flex align-items-center gap-2 flex-shrink-0'>
              <BulkVosFinanceFilterDropdown
                filterData={filterData}
                availableBatches={availableBatches}
                onApply={onFilterApply}
              />
              {!readOnly && batch && batch.status === 1 && (
                <button
                  type='button'
                  className='btn btn-primary btn-sm text-nowrap'
                  disabled={!canApproveAll || approving}
                  onClick={() => void approveAll()}
                >
                  {approving ? 'Approving…' : 'Approve all'}
                </button>
              )}
            </div>
          </div>
        </div>

        {batch && (
          <p className='text-muted fs-7 mt-3 mb-0'>
            {partnerPortalUrl && partnerPortalLinkLabel ? (
              <a
                href={partnerPortalUrl}
                target='_blank'
                rel='noopener noreferrer'
                className='bvp-finance-portal-link'
                title='Open partner portal (same link as the bulk email)'
              >
                {partnerPortalLinkLabel}
              </a>
            ) : (
              <>
                {batch.customerName && <span>{batch.customerName}</span>}
                {batch.customerName && periodLabel && <span> · </span>}
                {periodLabel && <span>Service period: {periodLabel}</span>}
              </>
            )}
            {batch.submittedAt && (
              <>
                <span> · </span>
                <span>Client submitted {formatSubmitted(batch.submittedAt)}</span>
              </>
            )}
            {batchApproved && (
              <>
                <span> · </span>
                <span className='badge badge-light-success fs-8'>Approved</span>
              </>
            )}
            {!batchApproved && batch.status === 1 && (
              <>
                <span> · </span>
                <span className='badge badge-light-warning fs-8'>Pending review</span>
              </>
            )}
          </p>
        )}

        <div className='mb-5 mt-3'>
          {!batch && !detailLoading ? (
            <div className='text-center text-muted py-10'>No batch selected or available.</div>
          ) : (
          <div className='table-responsive'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-3 gy-3'>
              <thead>
                <tr className='fw-semibold text-muted text-uppercase'>
                  <th className='min-w-80px'>Call ID</th>
                  <th className='min-w-150px'>Client name</th>
                  <th className='min-w-150px'>Requester</th>
                  <th className='min-w-100px'>Record ID</th>
                  <th className='min-w-100px'>Interpreter ID</th>
                  <th className='min-w-120px'>Assignment date</th>
                  <th className='min-w-100px'>Start time</th>
                  <th className='min-w-100px'>End time</th>
                  <th className='min-w-100px'>Duration</th>
                  <th className='min-w-180px'>Remarks</th>
                  <th className='min-w-100px text-end'>Action</th>
                </tr>
              </thead>
              <tbody>
                {detailLoading ? (
                  <tr>
                    <td colSpan={11} className='position-relative p-0' style={{height: '220px'}}>
                      <CommonLoading contained />
                    </td>
                  </tr>
                ) : paginatedRows.length === 0 ? (
                  <tr>
                    <td colSpan={11} className='text-center text-muted py-10'>
                      No appointments match this view.
                    </td>
                  </tr>
                ) : (
                  paginatedRows.map((item) => {
                    const draft = getDraft(item)
                    const disputed = isRowDisputed(item, batch?.status)
                    const startDisplay = formatBulkVosGridTime(
                      disputed ? item.startTime ?? draft.editableStartTime : item.startTime,
                    )
                    const endDisplay = formatBulkVosGridTime(
                      disputed ? item.endTime ?? draft.editableEndTime : item.endTime,
                    )
                    const remarksDisplay = resolveFinanceRemarks(item, draft, disputed)
                    const action = financeRowAction(item, batch?.status, batchApproved)

                    return (
                      <tr key={item.id} className={disputed ? 'bg-light-danger' : undefined}>
                        <td>
                          <span className='text-gray-800 d-block fs-6'>
                            {formatBulkVosCallId(item.callId)}
                          </span>
                        </td>
                        <td>
                          <span className='text-gray-800 d-block fs-6'>
                            {batch?.customerName?.trim() || '—'}
                          </span>
                        </td>
                        <td>
                          <span className='text-gray-800 d-block fs-6'>
                            {item.requesterName ?? '—'}
                          </span>
                        </td>
                        <td>
                          <span
                            className='text-gray-800 d-block fs-6 text-truncate'
                            style={{maxWidth: 120}}
                            title={item.recordId}
                          >
                            {formatBulkVosRecordId(item.recordId)}
                          </span>
                        </td>
                        <td>
                          <span className='text-gray-800 d-block fs-6'>
                            {formatBulkVosInterpreterId(item.interpreterId)}
                          </span>
                        </td>
                        <td>
                          <span className='text-gray-800 d-block fs-6'>
                            {item.assignmentDate ?? '—'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`d-block fs-6 ${disputed ? 'text-danger fw-semibold' : 'text-gray-800'}`}
                          >
                            {startDisplay ?? '—'}
                          </span>
                        </td>
                        <td>
                          <span
                            className={`d-block fs-6 ${disputed ? 'text-danger fw-semibold' : 'text-gray-800'}`}
                          >
                            {endDisplay ?? '—'}
                          </span>
                        </td>
                        <td>
                          {disputed ? (
                            <span className='badge badge-light-danger fs-6 text-danger fw-semibold px-3 py-2'>
                              {item.duration}
                            </span>
                          ) : (
                            <span className='badge badge-light fs-6 text-gray-700 fw-semibold px-3 py-2'>
                              {item.duration ?? '—'}
                            </span>
                          )}
                        </td>
                        <td>
                          <FinanceRemarksCell text={remarksDisplay} rowKey={item.id} />
                        </td>
                        <td className='text-end'>
                          <div className='bvp-finance-action-cell'>
                            {action === 'dispute' && (
                              <span className='bvp-finance-status-pill bvp-finance-status-pill--dispute'>
                                <i className='bi bi-exclamation-circle-fill' aria-hidden />
                                Dispute
                              </span>
                            )}
                            {action === 'sent-pending' && (
                              <span className='bvp-finance-status-pill bvp-finance-status-pill--sent-pending'>
                                <i className='bi bi-send' aria-hidden />
                                Sent - Pending
                              </span>
                            )}
                            {action === 'accepted' && (
                              <span className='bvp-finance-status-pill bvp-finance-status-pill--accepted'>
                                <i className='bi bi-check-circle-fill' aria-hidden />
                                Accepted
                              </span>
                            )}
                            {action === 'approved' && (
                              <span className='bvp-finance-status-pill bvp-finance-status-pill--accepted'>
                                <i className='bi bi-check-circle-fill' aria-hidden />
                                Approved
                              </span>
                            )}
                            <BulkVosVosFormViewIcon
                              item={item}
                              className='bvp-finance-edit-btn bvp-vos-doc-btn'
                            />
                            {canEditFinanceRows && (
                              <button
                                type='button'
                                className='bvp-finance-edit-btn'
                                aria-label='Edit VOS details'
                                title='Edit VOS details'
                                onClick={() => openFinanceItemModal(item)}
                              >
                                <KTSVG
                                  path='/media/icons/duotune/general/gen055.svg'
                                  className='svg-icon-3'
                                />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    )
                  })
                )}
              </tbody>
            </table>
          </div>
          )}
          {batch && !detailLoading && totalItems > 0 && (
            <CommonPaginationModel
              currentPage={currentPage}
              totalPages={totalPages}
              rowsPerPage={rowsPerPage}
              total={totalItems}
              onPageChange={(newPage) => setCurrentPage(newPage)}
              onRowsPerPageChange={(newRowsPerPage) => {
                setRowsPerPage(newRowsPerPage)
                setCurrentPage(1)
              }}
            />
          )}
        </div>

        {showStatusBar && (
          <p className='text-muted fs-7 pb-2 mb-0' aria-live='polite'>
            {footerNote}
            <span className='mx-2'>|</span>
            <span className='fw-semibold text-gray-800'>{disputedCount}</span> disputed
          </p>
        )}
      </div>

      <BulkVosOsiEditLauncher
        item={osiEditItem}
        onClose={closeOsiEdit}
        onReload={onReload}
      />
    </>
  )
}
